<template>
    <div class="mail-sidebar">
        <MailSettings
            :show="settingsModal"
            :isGridMode="isGridMode"
            @close="settingsModal = false"
            @layout-mode-changed="$emit('layout-mode-changed', $event)"
        />
        <div class="sidebar-content">
            <!-- Yeni mail oluşturma butonu -->
            <div class="compose-wrapper">
                <SupsisButton primary block @click="$emit('create-new-mail')" class="compose-button">
                    <i class="material-icons">add</i>
                    <span>{{ $t("mail.newMail") }}</span>
                </SupsisButton>
            </div>

            <!-- Mail hesabı seçimi - showOnlyFolders false ise görünecek -->
            <div class="mail-account-selector" v-if="!showOnlyFolders">
                <div class="selector-container">
                    <a-select
                        v-model="currentEmailId"
                        placeholder="Mail hesabı seçin"
                        class="account-select"
                        show-search
                        option-filter-prop="label"
                        @change="selectEmail($event, 'self')"
                    >
                        <a-select-option
                            v-for="email in emailAddresses"
                            :key="email._id"
                            :value="email._id"
                            :label="email.mail"
                        >
                            <span 
                                class="email-option-text"
                                :title="email.mail"
                            >{{ email.mail }}</span>
                        </a-select-option>
                    </a-select>
                    <SupsisButton primary square @click="settingsModal = true" class="settings-button">
                        <i class="material-icons">settings</i>
                    </SupsisButton>
                </div>
            </div>

            <!-- Ana mail klasörleri - Her zaman görünecek, ancak user/dept mail seçiliyse active class uygulanmayacak -->
            <div class="sidebar-section folder-section">
                <div class="section-title">{{ $t("mail.folders") }}</div>
                <div class="menu-items">
                    <div
                        v-for="folder in folders"
                        :key="folder.id"
                        class="menu-item"
                        :class="{ active: selectedFolder === folder.id && !isUserOrDeptSelected }"
                        @click="selectFolder(folder.id)"
                    >
                        <div class="item-icon">
                            <i class="material-icons">{{ folder.icon }}</i>
                        </div>
                        <div class="item-label">{{ folder.name }}</div>
                    </div>
                </div>
            </div>

            <!-- Kullanıcı mailleri listesi -->
            <div class="sidebar-section user-section" v-if="userEmails.length > 0 && !showOnlyFolders">
                <div class="section-title">
                    <span>{{ $t("chatbotv2.users") }}</span>
                </div>
                <div class="menu-items">
                    <div
                        v-for="email in userEmails"
                        :key="email._id"
                        class="menu-item horizontal"
                        :class="{ active: isUserOrDeptSelected && selectedEmailId === email._id }"
                        @click="selectEmail(email._id, 'user')"
                    >
                        <div class="item-icon">
                            <i class="material-icons">person</i>
                        </div>
                        <div class="user-info">
                            <div class="user-name">{{ getUserMailName(email) }}</div>
                            <div class="user-email">{{ email.mail }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Departman mailleri listesi -->
            <div class="sidebar-section department-section" v-if="departmentEmails.length > 0 && !showOnlyFolders">
                <div class="section-title">
                    <span>{{ $t("message.departments") }}</span>
                </div>
                <div class="menu-items">
                    <div
                        v-for="email in departmentEmails"
                        :key="email._id"
                        class="menu-item horizontal"
                        :class="{ active: isUserOrDeptSelected && selectedEmailId === email._id }"
                        @click="selectEmail(email._id, 'department')"
                    >
                        <div class="item-icon">
                            <i class="material-icons">groups</i>
                        </div>
                        <div class="user-info">
                            <div class="user-name">{{ getDepartmentMailName(email) }}</div>
                            <div class="user-email">{{ email.mail }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import MailSettings from "./MailSettings.vue";

export default {
    name: "MailSidebar",
    components: {
        MailSettings,
        Modal2,
        SupsisButton,
    },
    props: {
        selectedFolder: {
            type: String,
            required: true,
        },
        selectedEmailId: {
            type: String,
            default: null,
        },
        isGridMode: {
            type: Boolean,
            default: true,
        },
        userEmails: {
            type: Array,
            default: () => [],
        },
        departmentEmails: {
            type: Array,
            default: () => [],
        },
        emailAddresses: {
            type: Array,
            default: () => [],
        },
        showOnlyFolders: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            settingsModal: false,
            currentEmailId: this.selectedEmailId,
            isUserOrDeptSelected: false,
            lastRegularEmail: this.selectedEmailId,
            folders: [
                { id: "inbox", name: this.$t("mail.inbox"), icon: "inbox", count: 7 },
                { id: "sent", name: this.$t("mail.sent"), icon: "send", count: 0 },
            ],
        };
    },
    watch: {
        selectedEmailId(newVal) {
            if (!this.isUserOrDeptSelected) {
                this.currentEmailId = newVal;
                this.lastRegularEmail = newVal;
            }
        },
    },
    methods: {
        getUserMailName(mail) {
            return this.$_.get(mail, "user.name", this.$_.get(mail, "user.username", "Deleted User"));
        },
        getDepartmentMailName(mail) {
            return this.$_.get(mail, "department.languageContent[0].name", "Deleted Department");
        },

        selectFolder(folderId) {
            this.isUserOrDeptSelected = false;
            this.currentEmailId = this.lastRegularEmail;
            this.$emit("email-selected", this.lastRegularEmail);
            this.$emit("folder-selected", folderId);
            this.$emit("change-mail-type", "self");
        },
        selectEmail(emailAddress, type) {
            const isUserOrDept = type === "user" || type === "department";
            if(!isUserOrDept) this.lastRegularEmail = emailAddress;
            this.isUserOrDeptSelected = isUserOrDept;
            this.$emit("email-selected", emailAddress);
            this.$emit("change-mail-type", type);
        },
    },
};
</script>

<style lang="scss" scoped>
.mail-sidebar {
    width: 280px;
    min-width: 280px;
    height: 100%;
    background-color: #fff;
    border-right: 1px solid #eaeaea;
    overflow: auto;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    position: relative;

    /* Özel scrollbar stilleri */
    &::-webkit-scrollbar {
        width: 4px;
        /* Scrollbar genişliği 6px'den 4px'e düşürüldü */
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        /* Scrollbar arka plan rengi */
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
        background: #22272d;
        /* Scrollbar rengi */
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #1a1f23;
        /* Hover durumunda scrollbar rengi */
    }

    /* Firefox için scrollbar stilleri */
    scrollbar-width: thin;
    scrollbar-color: #22272d #f1f1f1;

    .sidebar-content {
        padding: 16px 0;
    }

    .compose-wrapper {
        padding: 0 16px 16px;
        position: relative;
        width: 100%;

        .compose-button {
            height: 48px;
            font-size: 15px;
            font-weight: 500;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            border-radius: 6px;

            i {
                font-size: 20px;
            }
        }
    }

    .mail-account-selector {
        padding: 0 16px 16px;

        .selector-container {
            display: flex;
            gap: 12px;
            align-items: center;
            width: 100%;
            padding-right: 4px;
        }

        .account-select {
            flex: 1;
            max-width: calc(100% - 44px); // Settings butonu ve gap için alan bırakıyoruz

            :deep(.ant-select-selector) {
                height: 42px;
                padding: 0 12px;
                display: flex;
                align-items: center;
                border-radius: 6px;
                border-color: #e0e0e0;
            }

            :deep(.ant-select-selection-item) {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
            }

            :deep(.ant-select-selection-search-input) {
                height: 40px;
            }

            .email-option-text {
                display: block;
                width: 100%;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
                cursor: pointer;
                position: relative;
            }

            :deep(.ant-select-item-option-content) {
                width: 100%;
                display: block;
            }

            :deep(.ant-select-item) {
                padding: 8px 12px !important;
            }
        }

        .settings-button {
            width: 32px;
            height: 32px;
            min-width: 32px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            border-radius: 6px;

            i {
                font-size: 18px;
            }
        }
    }

    .sidebar-section {
        margin-bottom: 16px;

        .section-title {
            display: flex;
            align-items: center;
            padding: 8px 16px;
            font-size: 13px;
            font-weight: 600;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            user-select: none;
        }

        .menu-items {
            padding: 2px 0;
        }

        .menu-item {
            display: flex;
            align-items: center;
            padding: 10px 16px;
            cursor: pointer;
            position: relative;
            margin: 0 6px;
            border-radius: 6px;
            transition: all 0.2s;

            &:hover {
                background-color: #f7f7f7;
            }

            &.active {
                background-color: #f5f5f5;
                position: relative;

                &::before {
                    content: "";
                    position: absolute;
                    left: 0;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 4px;
                    height: 20px;
                    background-color: #555;
                    border-radius: 0 4px 4px 0;
                }

                .item-label,
                .user-name {
                    color: #22272d;
                    font-weight: 500;
                }

                .item-icon {
                    background-color: #eee;

                    i {
                        color: #444;
                    }
                }
            }

            &.horizontal {
                padding: 10px 12px;

                .item-icon {
                    width: 36px;
                    height: 36px;
                    margin-right: 12px;
                }

                .user-info {
                    flex: 1;
                    overflow: hidden;

                    .user-name {
                        font-size: 14px;
                        color: #444;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        margin-bottom: 2px;
                    }

                    .user-email {
                        font-size: 12px;
                        color: #888;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
            }

            .item-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                min-width: 32px;
                border-radius: 50%;
                background-color: #f5f5f5;
                margin-right: 12px;
                transition: all 0.2s;

                i {
                    font-size: 18px;
                    color: #666;
                    transition: color 0.2s;
                }

                &.small {
                    width: 28px;
                    height: 28px;

                    i {
                        font-size: 16px;
                    }
                }
            }

            .item-label {
                font-size: 14px;
                color: #444;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                flex: 1;
            }
        }
    }
}

@media (max-width: 1200px) {
    .mail-sidebar {
        width: 260px;
        min-width: 260px;
    }
}

@media (max-width: 992px) {
    .mail-sidebar {
        width: 240px;
        min-width: 240px;
    }
}
</style>
